import { Box, Loader, LoadingOverlay, Stack, Text } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFlowStore } from "../../../../stores/WorkflowStore";
import { useLanguage } from "../../../../stores/LanguageStore";
import { clearWorkflow, handleWorkflowGet } from "./flow/flowUtils";
import RequiredActions from "../../account/RequiredActions/RequiredActions";
import LinkingNotification from "./flow/components/LinkingNotification";
import { useUIStore } from "../../../../stores/UIStore";
import WorkflowHeading from "./heading/WorkflowHeading";
import Flow from "./flow/Flow";
import Contacts from "./contacts/Contacts";
import Review from "./review/Review";
import ProgrammationStep from "./campaignSettings/ProgrammationStep";
import { useCampaignStore } from "../../../../stores/CampaignStore";
import { getNodesError } from "./flow/layouter";
import { defaultProgramData } from "./flow/flowSettings";
import { addHoursToDate, getTimezoneOffset } from "../../../../functions/dates";
import {
  createWorkflow,
  editWorkflow,
  sendWorkflowImage,
} from "../../../../api/WorkflowAPI";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import NodeErrors from "./flow/components/NodeErrors";
import { getSharings } from "../../../../api/PRMAPI";
import { findCampaign } from "../../../../api/StatsAPI";
import { useProfileStore } from "../../../../stores/UserStore";
import WorkflowPreview from "./flow/components/WorkflowPreview";
import { getStepsFromNodes } from "../../../../functions/utilFunctions";
import FlowHorizontal from "./flowHorizontal/FlowHorizontal";
import StepAgent from "./ai-flow/StepAgent";
import StepAudience from "./ai-flow/StepAudience";
import StepStrategy from "./ai-flow/StepStrategy";
import WorkflowHeadingAi from "./heading/WorkflowHeadingAi";
import StepRecap from "./ai-flow/StepRecap";
import StepContext from "./ai-flow/StepTheme";

const CreateCampaign = ({ isHorizontal }) => {
  const lang = useLanguage((s) => s.language);
  const { campaignId, workflowId } = useParams();
  const [active, setActive] = useState("workflow");
  const [isAi, setIsAi] = useState(null);
  const layout = useFlowStore((s) => s.layout);
  const loadingPage = useFlowStore((s) => s.loadingPage);
  const setLoadingPage = useFlowStore((s) => s.setLoadingPage);
  const savingWorkflow = useFlowStore((s) => s.savingWorkflow);
  const setSavingWorkflow = useFlowStore((s) => s.setSavingWorkflow);
  const setWorkflowError = useFlowStore((s) => s.setWorkflowError);
  const workflowKey = useFlowStore((s) => s.workflowKey);
  const [flowInstance, setFlowInstance] = useState(null);

  const direction = isHorizontal ? "horizontal" : layout;

  const handleActive = useCallback(() => {
    if (active === "workflow") {
      if (direction === "horizontal") {
        return (
          <FlowHorizontal
            key={workflowKey}
            setFlowInstance={setFlowInstance}
            setIsAi={setIsAi}
            isAi={isAi}
            setActive={setActive}
          />
        );
      } else {
        return <Flow key={workflowKey} setFlowInstance={setFlowInstance} />;
      }
    }
    if (active === "contacts") {
      return <Contacts setActive={setActive} key={workflowKey} />;
    }
    if (active === "preview") {
      return <Review setActive={setActive} key={workflowKey} />;
    }
    if (active === "program") {
      if (isAi) {
        return <StepRecap setActive={setActive} key={workflowKey} />;
      }
      return <ProgrammationStep setActive={setActive} key={workflowKey} />;
    }

    // AI
    if (active === "agent") {
      return <StepAgent setActive={setActive} key={workflowKey} />;
    }
    if (active === "theme") {
      return <StepContext setActive={setActive} key={workflowKey} />;
    }
    if (active === "audience") {
      return <StepAudience setActive={setActive} key={workflowKey} />;
    }
    if (active === "strategy") {
      return <StepStrategy setActive={setActive} key={workflowKey} />;
    }
  }, [active, workflowKey, direction, isAi]);

  // Save Workflow
  const navigate = useNavigate();
  useEffect(() => {
    const wProfile = useFlowStore.getState().profile;
    const allNodes = useFlowStore.getState().nodes;

    let initialId = useFlowStore.getState().initialId;

    let needDummy = allNodes.find(
      (x) => x?.data?.source === initialId && x?.data?.actionType === "event",
    );

    let nodes = allNodes.filter((x) => {
      if (needDummy) {
        return x.type === "action" || x.type === "start";
      } else {
        return x.type === "action";
      }
    });

    async function main() {
      modals.closeAll();
      setWorkflowError(false);

      const steps = getStepsFromNodes({ nodes, needDummy });

      function savePreview(defaultId) {
        const svgElement = document.getElementById("workflow-preview");
        if (svgElement) {
          const svgString = new XMLSerializer().serializeToString(svgElement);

          const blob = new Blob([svgString], { type: "image/svg+xml" });

          const formData = new FormData();
          formData.append("image", blob, "workflow.svg");

          sendWorkflowImage({
            id: defaultId || useFlowStore.getState().profile?.id,
            formData,
          });
        }
      }

      if (!wProfile?.id) {
        createWorkflow({
          steps: steps,
          auto_remove_responders: !!wProfile?.auto_remove_responders,
          name: wProfile?.name,
        })
          .then((res) => {
            const setProfile = useFlowStore.getState().setProfile;
            setProfile({
              id: res.data.workflow_id,
              auto_remove_responders: !!wProfile?.auto_remove_responders,
              name: wProfile?.name,
            });

            savePreview(res.data.workflow_id);

            notifications.show({
              message: lang.campaign.saved,
              color: "var(--mantine-color-green-8)",
            });

            let toSave = useFlowStore.getState().toSave;
            let setToSave = useFlowStore.getState().setToSave;

            if (toSave === "next") {
              console.log("e");

              navigate(
                `/campaign/create/workflow/${
                  res.data.workflow_id
                }?step=${isAi ? "preview" : "contacts"}&noReload`,
              );
            } else {
              navigate("/campaign/create/workflow/" + res.data.workflow_id);
              setToSave(null);
            }
          })
          .finally(() => {
            setSavingWorkflow(false);
          });
      } else {
        editWorkflow({
          id: wProfile?.id,
          steps: steps,
          auto_remove_responders: !!wProfile?.auto_remove_responders,
          name: wProfile?.name,
        })
          .then((res) => {
            notifications.show({
              message: lang.campaign.saved,
              color: "tertiary.7",
            });

            savePreview();

            const setProfile = useFlowStore.getState().setProfile;
            setProfile({
              id: wProfile?.id,
              auto_remove_responders: !!wProfile?.auto_remove_responders,
              name: wProfile?.name,
            });

            let toSave = useFlowStore.getState().toSave;
            let setToSave = useFlowStore.getState().setToSave;

            if (toSave === "next") {
              setActive(
                useFlowStore.getState().toStep ||
                  (isAi ? "preview" : "contacts"),
              );
              useFlowStore.getState().setToStep(null);
            } else {
              setLoadingPage(true);
              handleWorkflowGet({
                id: wProfile?.id,
                isEdit: true,
                onFinish: ({ newNodes }) => {
                  setLoadingPage(false);
                },
              });
              setToSave(null);
            }
          })
          .finally(() => setSavingWorkflow(false));
      }
    }

    if (savingWorkflow === true) {
      const nodesErrors = getNodesError({
        nodes: nodes.filter((x) => x.type === "action"),
        exclude_responders: !!wProfile?.auto_remove_responders,
      })?.filter((x) => x.errors.length > 0);

      if (nodesErrors?.length > 0) {
        modals.open({
          title: lang.campaign.nodes.errors.dialog_title,
          children: (
            <NodeErrors
              errors={nodesErrors}
              handleContinue={main}
              setActive={setActive}
            />
          ),
          closeOnClickOutside: false,
          size: "45%",
          radius: "lg",
          styles: {
            content: {
              boxShadow: "red 0 0 12px",
              border: "1px solid red",
            },
          },
          centered: true,
          withCloseButton: false,
        });
      } else {
        main();
      }
    }
  }, [lang, savingWorkflow]);

  // Initialization
  useEffect(() => {
    if (flowInstance?.viewportInitialized) {
      const workflow_id = parseInt(workflowId);
      const param = new URLSearchParams(window.location.search);
      const libraryId = param.get("libraryId");
      const toStep = param.get("step");
      const defaultList = param.get("list");
      const noReload = param.get("noReload");
      const isReprog = param.get("reprogram");
      useFlowStore.getState().setSelectedNode(null);
      const programData = useCampaignStore.getState().programData;
      const setProgramData = useCampaignStore.getState().setProgramData;
      const setOldProgramData = useCampaignStore.getState().setOldProgramData;

      if (defaultList) {
        setProgramData({
          ...programData,
          contactlist_ids: defaultList ? [parseInt(defaultList)] : [],
        });
      }
      if (libraryId) {
        setTimeout(() => {
          handleWorkflowGet({
            libraryId: libraryId,
            isEdit: false,
          });
        }, 50);
      }
      if (workflow_id) {
        if (!noReload) {
          setTimeout(() => {
            handleWorkflowGet({
              id: parseInt(workflow_id),
              isEdit: true,
              onFinish: ({ newNodes }) => {
                setLoadingPage(false);

                if (toStep) {
                  setActive(toStep || "workflow");
                }

                // let startingNode = newNodes?.find(
                //   (x) => x.data.initial === true
                // );

                // if (startingNode) {
                //   let x = startingNode.position.x + 128;
                //   let y =
                //     startingNode.position.y +
                //     (window.innerHeight / 2 -
                //       (startingNode.type === "action" ? 140 : 36));

                //   flowInstance.setCenter(x, y, { duration: 500 });
                // }
              },
            });
          }, 50);
        } else {
          setActive(toStep);
        }
      }
      if (campaignId && !programData?.id) {
        getSharings();
        findCampaign(workflow_id, campaignId).then((response) => {
          let config = response.data.workflow_programmation_profile;

          let timezoneOffset = getTimezoneOffset(config?.time_sending_timezone);
          let times = {};

          let date_start = useProfileStore.getState().profile?.timezone
            ? addHoursToDate(
                config.date_start,
                -timezoneOffset + new Date().getTimezoneOffset() / 60,
              )
            : new Date(config.date_start).toISOString();

          // let time_start_sending = incrementHour(config?.time_start_sending, timezoneOffset - new Date().getTimezoneOffset() / 60);
          // let time_stop_sending = incrementHour(config?.time_stop_sending, timezoneOffset - new Date().getTimezoneOffset() / 60);

          // console.log(timezoneOffset);
          // console.log(new Date().getTimezoneOffset() / 60);
          // config.contactlist_ids = isReprog ? [] : config.contactlist_ids;

          const setTimeStart = useCampaignStore.getState().setTimeStart;
          setTimeStart(config.time_start_sending);
          const setTimeStop = useCampaignStore.getState().setTimeStop;
          setTimeStop(config.time_stop_sending);

          setProgramData({
            ...config,
            ...times,
            date_start,
            // time_start_sending,
            // time_stop_sending
          });
          setOldProgramData({
            ...config,
            ...times,
            date_start,
            // time_start_sending,
            // time_stop_sending
          });
          useFlowStore.getState().setSharing(config?.stats_sharings || []);
        });
      } else {
        setLoadingPage(false);
      }
    }
  }, [campaignId, workflowId, flowInstance?.viewportInitialized]);

  useEffect(() => {
    const setProgramData = useCampaignStore.getState().setProgramData;
    const setTimeStart = useCampaignStore.getState().setTimeStart;
    const setTimeStop = useCampaignStore.getState().setTimeStop;
    let locked = useUIStore.getState().pinnedNavbar;
    if (!locked) {
      useUIStore.getState().setNavbarClosed(true);
      useUIStore.getState().setCampaignOpened(false);
    }
    useUIStore.getState().setHeaderClosed(true);
    return () => {
      clearWorkflow();

      useUIStore.getState().setHeaderClosed(false);
      setProgramData(defaultProgramData);
      setTimeStart(defaultProgramData.time_start_sending);
      setTimeStop(defaultProgramData.time_stop_sending);
      setIsAi(null);
    };
  }, []);

  useEffect(() => {
    setActive("workflow");
  }, [workflowKey]);

  // useEffect(() => {
  //   if (direction === "horizontal") {
  //     useFlowStore.getState().setInitialId("0");
  //   } else {
  //     useFlowStore.getState().setLayout("vertical");
  //   }
  // }, [direction])

  return (
    <>
      <LoadingOverlay overlayProps={{ blur: 1 }} visible={loadingPage} />

      <LoadingOverlay
        overlayProps={{ blur: 1 }}
        visible={savingWorkflow}
        loaderProps={{
          children: (
            <Stack align="center" gap={"xs"}>
              <Loader />
              <Box w={"60vw"} h={"60vh"}>
                <WorkflowPreview />
              </Box>

              <Text size="lg">{lang.campaigns.saving}</Text>
            </Stack>
          ),
        }}
      />

      {isAi ? (
        <WorkflowHeadingAi activeStep={active} setActiveStep={setActive} />
      ) : (
        <WorkflowHeading activeStep={active} setActiveStep={setActive} />
      )}

      <Stack flex={"1 1 auto"} h={0} pl={18}>
        {handleActive()}
      </Stack>

      <RequiredActions />

      <LinkingNotification />
    </>
  );
};

export default CreateCampaign;
